import { WorkLogStatus } from "@wingspanhq/wingspan-api-sdk";
import { WORK_LOG_STATUS_DISCARDED } from "../types";

/**
 * WorkLogStatus values for consistent status checking.
 */
export const WorkLogStatusValues = {
  Draft: WorkLogStatus.DRAFT,
  InProgress: WorkLogStatus.IN_PROGRESS,
  InReview: WorkLogStatus.IN_REVIEW,
  Approved: WorkLogStatus.APPROVED,
  Rejected: WorkLogStatus.REJECTED,
  Discarded: WORK_LOG_STATUS_DISCARDED
} as const;

// --- Status checks ---

export function isStatusDraft(status?: WorkLogStatus): boolean {
  return status === WorkLogStatusValues.Draft;
}

export function isStatusInProgress(status?: WorkLogStatus): boolean {
  return status === WorkLogStatusValues.InProgress;
}

export function isStatusInReview(status?: WorkLogStatus): boolean {
  return status === WorkLogStatusValues.InReview;
}

export function isStatusApproved(status?: WorkLogStatus): boolean {
  return status === WorkLogStatusValues.Approved;
}

export function isStatusRejected(status?: WorkLogStatus): boolean {
  return status === WorkLogStatusValues.Rejected;
}

// "Discarded" is not in the SDK WorkLogStatus enum yet, so accept a raw string.
export function isStatusDiscarded(status?: WorkLogStatus | string): boolean {
  return (status as string) === WorkLogStatusValues.Discarded;
}

// --- Payee permissions ---
// Based on Authorization Matrix in FRONTEND_IMPLEMENTATION_PLAN.md

export function canPayeeEdit(status?: WorkLogStatus): boolean {
  if (!status) return false;
  return isStatusDraft(status) || isStatusInProgress(status);
}

export function canPayeeSubmit(status?: WorkLogStatus): boolean {
  if (!status) return false;
  return isStatusDraft(status) || isStatusInProgress(status);
}

export function canPayeeDelete(status?: WorkLogStatus): boolean {
  if (!status) return false;
  return isStatusDraft(status);
}

export function canPayeeRequestEditing(status?: WorkLogStatus): boolean {
  if (!status) return false;
  return isStatusInReview(status);
}

export function canPayeeRequestChanges(status?: WorkLogStatus): boolean {
  if (!status) return false;
  return isStatusApproved(status);
}

export function hasPayeeRequestedEditing(workLog?: {
  status?: WorkLogStatus;
  payeeOwnedData?: { comment?: string };
}): boolean {
  if (!workLog) return false;
  return (
    isStatusInReview(workLog.status) &&
    !!(workLog as any).payeeOwnedData?.comment
  );
}

// --- Payer permissions ---
// Based on Authorization Matrix in FRONTEND_IMPLEMENTATION_PLAN.md

export function canPayerApprove(status?: WorkLogStatus): boolean {
  if (!status) return false;
  return isStatusInReview(status);
}

export function canPayerReject(status?: WorkLogStatus): boolean {
  if (!status) return false;
  return isStatusInReview(status);
}

export function canPayerRequestChanges(status?: WorkLogStatus): boolean {
  if (!status) return false;
  return isStatusInReview(status);
}

export function canPayerEdit(status?: WorkLogStatus): boolean {
  if (!status) return false;
  return isStatusInReview(status);
}

export function canPayerReopen(status?: WorkLogStatus | string): boolean {
  if (!status) return false;
  return isStatusApproved(status as WorkLogStatus) || isStatusDiscarded(status);
}

// --- Last editor derivation ---

type WorkItemEditorInfo = {
  role: string;
  timestamp: Date;
};

/**
 * Finds the most recently edited work item and returns the editor's role.
 * Uses labels.updatedByRole with events.updatedAt, falling back to
 * labels.createdByRole with events.createdAt for items never updated.
 */
export function getWorkLogLastEditor(
  workItems?: Array<{
    events?: Record<string, string | Date | undefined>;
    labels?: Record<string, string | boolean | null | undefined>;
  }>
): WorkItemEditorInfo | null {
  if (!workItems || workItems.length === 0) return null;

  let latestTimestamp: Date | null = null;
  let latestRole: string | null = null;

  for (const item of workItems) {
    const updatedAt = item.events?.updatedAt;
    const updatedByRole = item.labels?.updatedByRole;

    if (updatedAt && typeof updatedByRole === "string") {
      const date = new Date(updatedAt as string | Date);
      if (!latestTimestamp || date > latestTimestamp) {
        latestTimestamp = date;
        latestRole = updatedByRole;
      }
    }

    if (!updatedAt) {
      const createdAt = item.events?.createdAt;
      const createdByRole = item.labels?.createdByRole;

      if (createdAt && typeof createdByRole === "string") {
        const date = new Date(createdAt as string | Date);
        if (!latestTimestamp || date > latestTimestamp) {
          latestTimestamp = date;
          latestRole = createdByRole;
        }
      }
    }
  }

  if (!latestRole || !latestTimestamp) return null;

  return { role: latestRole, timestamp: latestTimestamp };
}

/**
 * Resolves the last editor's display name from work item data.
 * Returns "You" if the current user's role matches the last editor,
 * the other party's name otherwise, or undefined if unresolvable.
 */
export function getLastEditorName(
  workItems?: Array<{
    events?: Record<string, string | Date | undefined>;
    labels?: Record<string, string | boolean | null | undefined>;
  }>,
  currentUserRole?: "payer" | "payee",
  otherPartyName?: string
): string | undefined {
  const lastEditor = getWorkLogLastEditor(workItems);
  if (!lastEditor || !currentUserRole) return undefined;

  if (lastEditor.role === currentUserRole) {
    return "You";
  }

  return otherPartyName;
}

/**
 * Returns true if the work item was edited after the work log was last submitted.
 * When either timestamp is unavailable, defaults to true (preserving existing behavior).
 */
export function isEditedAfterLastSubmission(
  lastEditedAt?: Date | string | null,
  lastSubmittedAt?: Date | string | null
): boolean {
  if (!lastEditedAt || !lastSubmittedAt) return true;
  return new Date(lastEditedAt) > new Date(lastSubmittedAt);
}

/**
 * Resolves the editor's display name for a single work item.
 * Returns "You" if the current user's role matches the editor,
 * the other party's name otherwise, or undefined if unresolvable.
 */
export function getWorkItemEditorName(
  workItem?: {
    events?: Record<string, string | Date | undefined>;
    labels?: Record<string, string | boolean | null | undefined>;
  },
  currentUserRole?: "payer" | "payee",
  otherPartyName?: string
): string | undefined {
  if (!workItem || !currentUserRole) return undefined;

  const role =
    (workItem.labels?.updatedByRole as string) ||
    (workItem.labels?.createdByRole as string);

  if (!role) return undefined;

  if (role === currentUserRole) {
    return "You";
  }

  return otherPartyName;
}
